import React, { useState, useEffect } from "react";
import { apiService } from "../services/api";
import SearchableSelect from "./SearchableSelect";

// API response types
interface Country {
  id: string;
  code: string;
  name: string;
  phone_code?: string;
  currency_code?: string;
}

interface State {
  id: string;
  code: string;
  name: string;
}

interface City {
  id: string;
  name: string;
}

interface LocationSelectorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  onCountryChange?: (country: Country | null) => void;
  onLocationPartsChange?: (parts: {
    country: string;
    state: string;
    city: string;
  }) => void;
  renderAfterCountry?: React.ReactNode;
  countryError?: string;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  value,
  onChange,
  className = "",
  disabled = false,
  required = false,
  id,
  name,
  onCountryChange,
  onLocationPartsChange,
  renderAfterCountry,
  countryError,
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("");
  const [selectedCity, setSelectedCity] = useState<string>("");

  const [loading, setLoading] = useState({
    countries: false,
    states: false,
    cities: false,
  });

  // Load countries on component mount
  useEffect(() => {
    loadCountries();
  }, []);

  // Parse existing value on mount
  useEffect(() => {
    if (value) {
      const parts = value.split(", ");
      if (parts.length >= 3) {
        const [city, state, country] = parts;
        setSelectedCity(city);
        setSelectedState(state);
        setSelectedCountry(country);
      }
    }
  }, [value]);

  const loadCountries = async () => {
    setLoading((prev) => ({ ...prev, countries: true }));
    try {
      const countriesData = await apiService.getCountries();
      setCountries(countriesData);
    } catch (error) {
      console.error("Failed to load countries:", error);
    } finally {
      setLoading((prev) => ({ ...prev, countries: false }));
    }
  };

  const loadStatesAndCities = async (countryCode: string) => {
    // First try to load states
    setLoading((prev) => ({ ...prev, states: true }));
    try {
      const statesData = await apiService.getStates(countryCode);

      if (statesData && statesData.length > 0) {
        // Country has states - use normal flow
        setStates(statesData);
      } else {
        // Country has no states - load cities directly
        setStates([]);
        await loadCitiesDirectly(countryCode);
      }
    } catch (error) {
      console.error("Failed to load states:", error);
      setStates([]);
      // Try loading cities directly as fallback
      await loadCitiesDirectly(countryCode);
    } finally {
      setLoading((prev) => ({ ...prev, states: false }));
    }
  };

  const loadCitiesDirectly = async (countryCode: string) => {
    setLoading((prev) => ({ ...prev, cities: true }));
    try {
      const citiesData = await apiService.getCitiesForCountry(countryCode);
      setCities(citiesData);
    } catch (error) {
      console.error("Failed to load cities directly:", error);
      setCities([]);
    } finally {
      setLoading((prev) => ({ ...prev, cities: false }));
    }
  };

  const loadCities = async (countryCode: string, stateCode: string) => {
    setLoading((prev) => ({ ...prev, cities: true }));
    try {
      const citiesData = await apiService.getCities(countryCode, stateCode);
      setCities(citiesData);
    } catch (error) {
      console.error("Failed to load cities:", error);
      setCities([]);
    } finally {
      setLoading((prev) => ({ ...prev, cities: false }));
    }
  };

  const handleCountryChange = (countryCode: string) => {
    const country = countries.find((c) => c.code === countryCode);

    setSelectedCountry(country?.name || "");
    onCountryChange?.(country || null);
    setSelectedState("");
    setSelectedCity("");
    setStates([]);
    setCities([]);

    // Clear the final value since we're changing country
    onChange("");
    onLocationPartsChange?.({
      country: country?.name || "",
      state: "",
      city: "",
    });

    if (countryCode) {
      loadStatesAndCities(countryCode);
    }
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateCode = e.target.value;
    const state = states.find((s) => s.code === stateCode);
    const country = countries.find((c) => c.name === selectedCountry);

    setSelectedState(state?.name || "");
    setSelectedCity("");
    setCities([]);

    // Clear the final value since we're changing state
    onChange("");
    onLocationPartsChange?.({
      country: selectedCountry,
      state: state?.name || "",
      city: "",
    });

    if (stateCode && country) {
      loadCities(country.code, stateCode);
    }
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cityName = e.target.value;
    setSelectedCity(cityName);

    if (cityName && selectedCountry) {
      let fullLocation;
      if (selectedState) {
        // Country with states: City, State, Country
        fullLocation = `${cityName}, ${selectedState}, ${selectedCountry}`;
      } else {
        // Country without states: City, Country
        fullLocation = `${cityName}, ${selectedCountry}`;
      }
      onChange(fullLocation);
      onLocationPartsChange?.({
        country: selectedCountry,
        state: selectedState,
        city: cityName,
      });
    } else {
      onChange("");
      onLocationPartsChange?.({
        country: selectedCountry,
        state: selectedState,
        city: "",
      });
    }
  };

  // Check if current country has states
  const countryHasStates = states.length > 0;
  const showStateSelector = selectedCountry && countryHasStates;
  const showCitySelector =
    selectedCountry && (countryHasStates ? selectedState : true);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Country Selection */}
      <div>
        <SearchableSelect
          id={`${id}-country`}
          label="Country"
          value={countries.find((c) => c.name === selectedCountry)?.code || ""}
          onChange={handleCountryChange}
          options={countries.map((country) => ({
            label: country.name,
            value: country.code,
          }))}
          placeholder="Search country"
          loadingText="Loading countries..."
          noResultsText="No countries found"
          disabled={disabled}
          loading={loading.countries}
          required={false}
          showRequiredMark={required}
          labelClassName="block text-sm font-medium text-gray-700 mb-2"
          inputClassName={countryError ? "border-red-300 bg-red-50" : ""}
        />
        {countryError && (
          <p className="mt-1 text-xs text-red-600">{countryError}</p>
        )}
      </div>

      {renderAfterCountry}

      {/* State Selection - Only show if country has states */}
      {showStateSelector && (
        <div>
          <label
            htmlFor={`${id}-state`}
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            State/Province {required && <span className="text-red-500">*</span>}
          </label>
          <select
            id={`${id}-state`}
            name={`${name}-state`}
            value={states.find((s) => s.name === selectedState)?.code || ""}
            onChange={handleStateChange}
            disabled={disabled || loading.states}
            required={required}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {loading.states ? "Loading states..." : "Select a state/province"}
            </option>
            {states.map((state) => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* City Selection */}
      {showCitySelector && (
        <div>
          <label
            htmlFor={`${id}-city`}
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            City {required && <span className="text-red-500">*</span>}
          </label>
          <select
            id={`${id}-city`}
            name={`${name}-city`}
            value={selectedCity}
            onChange={handleCityChange}
            disabled={disabled || loading.cities}
            required={required}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {loading.cities ? "Loading cities..." : "Select a city"}
            </option>
            {cities.map((city) => (
              <option key={city.id} value={city.name}>
                {city.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Selected Location Display */}
      {value && (
        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          <p className="text-sm text-gray-600">Selected Location:</p>
          <p className="font-medium text-gray-900">{value}</p>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
